import { FeatureCards } from '@ldp-web/component-library/ui/organisms/feature-cards';
import { useAnalyticsContext, useFeatureFlags } from '@ldp/common-contexts';
import {
  IComponentCommonProps,
  IHeaderGroupComponentProps,
  TSDMCardScrollType,
  TSDMCardUpType,
  TSDMFeatureCardsList,
} from '@ldp/common-types';
import {
  SDMAnalyticEvents,
  TCTAContextAnalytics,
  TSDMComponentAnalyticsObject,
  TVerticalFeatureCardContainerCTAContext,
  TrackingFnContext,
  getCommonComponentContext,
} from '@ldp/sdm-analytics-utils';
import { useSDMRouterContext } from '@ldp/sdm-contexts';
import { useSimpleTranslation } from '@ldp/simple-i18n';
import { FC, ReactNode, useEffect, useState } from 'react';
import { useRedirectLink } from '../../utils';
import { HeaderGroupComponent } from '../header-group/header-group.component';
import { WrapperComponent } from '../wrapper';

type TSDMImageSize = 'DEFAULT' | 'LARGE';
type TVerticalFeatureCardItem = Omit<TSDMFeatureCardsList, 'displayText1'> & {
  displayText1?: string;
};

const IMAGE_SIZE_ASPECT_RATIO: Record<TSDMImageSize, number> = {
  DEFAULT: 16 / 9,
  LARGE: 3 / 4,
};

export interface IVerticalFeatureCardComponentProps extends IComponentCommonProps {
  children?: ReactNode;
  /** up number decides number of cards in a row */
  upNumber: TSDMCardUpType;
  componentId?: string;
  /** scroll type decides whether to show scroller or in grid */
  scrollOverflow: TSDMCardScrollType;
  /** feature cards list */
  verticalFeatureCards: TVerticalFeatureCardItem[];
  /** header group */
  headerGroup?: IHeaderGroupComponentProps;
  /** content id of component */
  contentId?: string;
  /** analytics object */
  analytics?: TSDMComponentAnalyticsObject;
  /** id of component */
  id?: string;
  /** all vertical feature cards */
  _allVerticalFeatureCards?: TVerticalFeatureCardItem[];
  /** controls image aspect ratio: DEFAULT (16:9) or LARGE (3:4) */
  imageSize?: TSDMImageSize;
}

/** Renders Vertical Feature Cards in a scroller or grid.
 * Mock Data will be replaced with props once actual data from BFF is ready */
export const VerticalFeatureCardComponent: FC<IVerticalFeatureCardComponentProps> = (props) => {
  const {
    upNumber,
    scrollOverflow,
    verticalFeatureCards,
    headerGroup,
    _allVerticalFeatureCards,
    styling,
    componentId,
    contentId,
    analytics,
    id,
    imageSize = 'DEFAULT',
  } = props;
  const i18n = useSimpleTranslation();
  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const scrollOverflowValue = scrollOverflow.toLowerCase() as TSDMCardScrollType;
  const aspectRatio = [IMAGE_SIZE_ASPECT_RATIO[imageSize] ?? IMAGE_SIZE_ASPECT_RATIO.DEFAULT];

  const [allVerticalFeatureCards, setAllVerticalFeatureCards] = useState(verticalFeatureCards);
  useEffect(() => {
    if (_allVerticalFeatureCards && _allVerticalFeatureCards.length > verticalFeatureCards.length) {
      setAllVerticalFeatureCards(_allVerticalFeatureCards);
    }
  }, [_allVerticalFeatureCards, verticalFeatureCards]);

  /** adds a new key called 'callToActionModal' to the object */
  const updatedCardsList = (allVerticalFeatureCards ?? verticalFeatureCards)
    .map((i) => {
      const featureCard = i as TVerticalFeatureCardItem;
      if (featureCard?.callToActionLink && 'modalTitle' in featureCard.callToActionLink) {
        const { callToActionLink, ...rest } = featureCard;
        return {
          ...rest,
          callToActionModal: callToActionLink,
        };
      } else {
        return { ...i };
      }
    })
    .filter((card) => card) as TVerticalFeatureCardItem[];

  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow: snowplowEnabled } = getFeatureFlags();

  const trackFnWrapper: TrackingFnContext = (type, context) => {
    const { actionType, callToActionElement, position } = context as TCTAContextAnalytics;
    const dsModelData = analytics?.dsModel;
    const engineValue = dsModelData?.engine || '';
    const commonContext = getCommonComponentContext(snowplowEnabled, true, dsModelData);
    const headlineText = headerGroup?.headline?.text || '';
    const featureCardIndex = position ? position - 1 : 0;
    const selectedFeatureCard = updatedCardsList[featureCardIndex];
    const { contentId: verticalFeatureCardContentId, headline: verticalFeatureCardHeadline } = selectedFeatureCard;

    const eventTrackingContext: TVerticalFeatureCardContainerCTAContext = {
      event: type as SDMAnalyticEvents,
      parentComponent: {
        id: contentId || '',
        type: componentId || '',
        name: headlineText,
        engine: engineValue,
      },
      component: {
        id: verticalFeatureCardContentId || '',
        type: 'verticalFeatureCards',
        name: verticalFeatureCardHeadline,
        click_element: 'callToAction',
        click_action: callToActionElement?.text,
        click_url: callToActionElement?.url,
        engine: engineValue,
        position: position,
        number_of_children: verticalFeatureCards.length,
        parent_id: contentId,
        hierarchy: [headlineText, verticalFeatureCardHeadline],
      },
      customEngagement: {
        action: actionType,
      },
      ...commonContext,
    };

    trackEvent('heliosVerticalFeatureCardContainer', eventTrackingContext);
  };

  /** set no padding for PLP only */
  const IS_SSR = typeof window === 'undefined';

  const isNoPadding = !IS_SSR
    ? window.location.pathname.includes('/c/') || window.location.pathname.includes('/plp')
    : false;

  /**
   * Avoid Emotion className hydration mismatch: FeatureCards uses Chakra Card (Emotion),
   * which can emit different class hashes on server vs client when there is no shared
   * Emotion cache. Render a stable placeholder until after client mount, then show
   * the real cards so server and first client paint match.
   */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <WrapperComponent id={id} data-testid="vertical-feature-cards" styling={styling} isNoPadding={isNoPadding}>
      {headerGroup && <HeaderGroupComponent {...headerGroup} noWrapper />}
      {mounted ? (
        <FeatureCards
          upNumber={upNumber}
          scrollType={scrollOverflowValue}
          cardsList={updatedCardsList as any}
          i18nskip={i18n('skip')}
          skipLinkLabel={i18n('a11ySection')}
          variant="vertical"
          cardClickAction={redirectToPage}
          trackingFn={trackFnWrapper}
          aspectRatio={aspectRatio}
        />
      ) : (
        <div
          data-testid="vertical-feature-cards-placeholder"
          style={{ minHeight: 200 }}
          aria-hidden="true"
        />
      )}
    </WrapperComponent>
  );
};

export default VerticalFeatureCardComponent;
